import { useRef } from "react";
import { Copy, ExternalLink, Upload, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const INPUT = "bg-[#0b0811] border-white/10 text-white";

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

// Onglet « Connexion » du Builder : adresse /login/<slug>, thème, textes, logo fond clair, mentions légales
export default function LoginPageSettings({ config, slug, setSlug, setBranding, uploadAsset }) {
  const b = config.branding;
  const logoLightRef = useRef(null);
  const coverRef = useRef(null);
  const loginUrl = `${window.location.origin}/login/${slug || "…"}`;

  const copy = () => { navigator.clipboard?.writeText(loginUrl); toast.success("Lien copié"); };

  return (
    <div className="space-y-4" data-testid="builder-panel-login">
      <Field label="Adresse de connexion de la société" hint="Lettres minuscules, chiffres et tirets. Chaque société a sa propre page de connexion à ses couleurs.">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 whitespace-nowrap">{window.location.origin}/login/</span>
          <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="ems" className={INPUT + " font-mono"} data-testid="builder-login-slug" />
        </div>
        <div className="flex gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={copy} disabled={!slug} className="border-white/10 text-slate-300 text-xs" data-testid="builder-login-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Copier le lien</Button>
          <Button variant="outline" size="sm" onClick={() => window.open(`/login/${slug}`, "_blank")} disabled={!slug} className="border-white/10 text-slate-300 text-xs" data-testid="builder-login-open"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Ouvrir la page</Button>
        </div>
      </Field>

      <Field label="Fond de la page de connexion">
        <div className="flex gap-2" data-testid="builder-login-theme">
          {[["dark", "Sombre", Moon], ["light", "Clair", Sun]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setBranding("login_theme", k)} data-testid={`builder-login-theme-${k}`}
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-bold py-2 rounded-lg border ${(b.login_theme || "dark") === k ? "border-[#f7c935] text-[#f7c935] bg-[#f7c935]/10" : "border-white/10 text-slate-400"}`}>
              <Icon className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Titre d'accueil (panneau de gauche)"><Input value={b.login_title || ""} onChange={(e) => setBranding("login_title", e.target.value)} placeholder={b.welcome_title} className={INPUT} data-testid="builder-login-title" /></Field>
      <Field label="Accroche"><Input value={b.login_tagline || ""} onChange={(e) => setBranding("login_tagline", e.target.value)} className={INPUT} data-testid="builder-login-tagline2" /></Field>
      <Field label="Police (optionnel)" hint="Ex. Inter, Montserrat — doit être disponible sur le poste ou chargée par le navigateur."><Input value={b.font_family || ""} onChange={(e) => setBranding("font_family", e.target.value)} placeholder="Inter" className={INPUT} data-testid="builder-login-font" /></Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Logo pour fond clair" hint="Utilisé quand le fond est clair (sinon le logo principal).">
          <input ref={logoLightRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset("logo_light", e.target.files?.[0])} data-testid="builder-logo-light-input" />
          <div className="flex items-center gap-2">
            {b.logo_light_url ? <img src={b.logo_light_url} alt="logo clair" className="h-9 max-w-[110px] object-contain rounded bg-white p-1" /> : <div className="h-9 w-20 rounded bg-white/5 border border-dashed border-white/10" />}
            <Button variant="outline" size="sm" onClick={() => logoLightRef.current?.click()} className="border-white/10 text-slate-300 text-xs" data-testid="builder-logo-light-btn"><Upload className="w-3.5 h-3.5 mr-1" /> Changer</Button>
          </div>
        </Field>
        <Field label="Visuel de gauche" hint="Image affichée sur le panneau gauche de la connexion.">
          <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset("cover", e.target.files?.[0])} data-testid="builder-login-cover-input" />
          <div className="flex items-center gap-2">
            {b.cover_url ? <img src={b.cover_url} alt="visuel" className="h-9 w-16 object-cover rounded" /> : <div className="h-9 w-16 rounded bg-white/5 border border-dashed border-white/10" />}
            <Button variant="outline" size="sm" onClick={() => coverRef.current?.click()} className="border-white/10 text-slate-300 text-xs" data-testid="builder-login-cover-btn"><Upload className="w-3.5 h-3.5 mr-1" /> Changer</Button>
          </div>
        </Field>
      </div>

      <Field label="Mentions légales (pied de page de la connexion)" hint="Raison sociale, forme juridique, capital, SIREN/RCS, TVA, adresse, NDA, contact…">
        <Textarea rows={5} value={b.legal_mentions || ""} onChange={(e) => setBranding("legal_mentions", e.target.value)} className={INPUT + " text-xs"} data-testid="builder-login-legal" />
      </Field>
    </div>
  );
}
